import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  FormControlLabel,
  Switch,
  TextField,
} from '@client/new/components/basic';
import { FieldTitle } from './RedStarTitle';
import {
  toProgramCheckoutUrl,
  toSubscriptionPurchasePage,
} from '@client/helpers/routes';
import { useCreator, useProgram } from './OTPLandingPageBuilder';
import { SecondaryButton } from '@client/new/components/button';
import { OfferDetailsData } from './OfferDetails.mappers';
/**
 * Note: this component is currently designed for Subscription, OTP program pages, and Offer landing pages
 */

type Props = {
  index: number;
  offerData?: OfferDetailsData;
};

const CTAFormComponent: React.FC<Props> = ({ index, offerData }) => {
  const { control, watch, setValue } = useFormContext();
  const ctaEnabledPath = `sections.${index}.cta.enabled`;
  const ctaCustomizePath = `sections.${index}.cta.customize`;
  const ctaEnabled = watch(ctaEnabledPath, true);
  const influencer = useCreator();
  const program = useProgram();

  // Determine if we're in offer mode
  const isOfferMode = !!offerData && !program;

  // Calculate default CTA values based on mode
  let defaultCtaLink: string;
  let ctaDefaultText: string;

  if (isOfferMode && offerData) {
    // Offer mode: use offer data
    const priceFormatted = offerData.price
      ? `$${(offerData.price / 100).toFixed(2)}`
      : '$0.00';
    defaultCtaLink = offerData.checkoutUrl || '#';
    ctaDefaultText = `BUY FOR ${priceFormatted}`;
  } else if (program && influencer) {
    // OTP/Subscription mode: use program data
    const isOtpProgram = program.accessType === 'exclusive';
    defaultCtaLink = isOtpProgram
      ? toProgramCheckoutUrl(program)
      : toSubscriptionPurchasePage(influencer);
    ctaDefaultText = isOtpProgram
      ? 'BUY NOW'
      : influencer.enableCheckoutFreeTrial
      ? 'START 7-DAY FREE TRIAL'
      : 'SUBSCRIBE';
  } else {
    // No data available - shouldn't happen, but handle gracefully
    return null;
  }

  const handleCtaEnable = (e: React.ChangeEvent<HTMLInputElement>) => {
    const wannaCheck = e.target.checked;
    setValue(ctaEnabledPath, wannaCheck);

    if (wannaCheck) {
      setValue(ctaCustomizePath, true);
      setValue(`sections.${index}.cta.text`, null);
      setValue(`sections.${index}.cta.link`, null);
    } else {
      setValue(ctaCustomizePath, false);
      setValue(`sections.${index}.cta.text`, null);
      setValue(`sections.${index}.cta.link`, null);
    }
  };

  const handleResetCta = () => {
    setValue(`sections.${index}.cta.text`, '');
    setValue(`sections.${index}.cta.link`, '');
    setValue(ctaCustomizePath, true);
  };

  return (
    <div className="mt-8">
      <div className="flex items-center">
        <FieldTitle title={'Call To Action'} optional />
        <Controller
          name={ctaEnabledPath}
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  className="ml-5"
                  {...field}
                  checked={ctaEnabled}
                  onChange={handleCtaEnable}
                />
              }
              label=""
            />
          )}
        />
      </div>

      {ctaEnabled && (
        <div className="space-y-3 max-w-md">
          <Controller
            name={`sections.${index}.cta.text`}
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Text" placeholder={ctaDefaultText} />
            )}
          />
          <Controller
            name={`sections.${index}.cta.link`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Link"
                type="url"
                inputProps={{ pattern: 'https?://.*' }}
                placeholder={defaultCtaLink}
                helperText="Link where users are directed. Defaults to checkout page."
              />
            )}
          />

          <SecondaryButton onClick={handleResetCta}>Reset</SecondaryButton>
        </div>
      )}
    </div>
  );
};

export default CTAFormComponent;
